import {
  AuditTrailServiceApiAuditTrailServiceActionsPerformedOnDayRequest,
  AuditTrailServiceApiAuditTrailServiceResourceActionsDailyRequest,
  V1ActionsPerformedOnDayResponse,
  V1ResourceActionsDailyResponse,
} from '@ariksa/audittrail';
import {
  AuditTrailServiceApiAuditTrailServiceActionsPerformedMonthlyRequest,
  AuditTrailServiceApiAuditTrailServiceResourceActionsMonthlyRequest,
  V1ActionsPerformedMonthlyResponse,
  V1ResourceActionsMonthlyResponse,
} from '@ariksa/audittrail/api';
import {
  AudittrailApiGetInsecureAclResponseRequest,
  AudittrailApiGetS3CrossAccountRequest,
  AudittrailApiGetS3InternetActivityRequest,
  BlueprintExceptionRead,
  ComplianceApiGetComplianceReportForResourceRequest,
  PageComplianceReport,
  PolicyExceptionApiAddExceptionRequest,
  S3CrossAccountAccess,
  S3InsecureACL,
  S3InternetActivity,
} from '@ariksa/compliance-policies/api';
import {
  DataTypeInfo,
  DataTypesApiGetDataTypesBySourceRequest,
  DistributionByDocumentType,
  ObjectsDistributionCount,
  PageResourcesRead,
  PageDocumentClassificationForSourceInfo,
  PageFileMetaDataInfo,
  RepositoryBranchesInfo,
  ResourcesApiGetObjectsDistributionCountRequest,
  ResourcesApiGetResourcesByGroupRequest,
  ResourcesApiGetSummarySourcesCountRequest,
  SourceCountSummary,
  VisibilityDataApiGetDocumentClassificationByTypeForSourceRequest,
  VisibilityDataApiGetDocumentClassificationTypeDistributionForSourceRequest,
  VisibilityDataApiGetFileMetadataInfoRequest,
  VisibilityDataApiGetRepositoryBranchesInfoRequest,
} from '@ariksa/data-scanning/api';
import { ConnectionByProcessPayload } from '@ariksa/ebpf-data-collector/api';
import {
  DeployedResourcePagination,
  IACApiDeployedResourcesRequest,
  IACApiDeploymetSummaryRequest,
} from '@ariksa/inventory-core';
import {
  AccessByIdentityPage,
  ContextApiGetRiskContextDataScanRequest,
  ActionsResponse,
  InsightApiGetAccessByIdentityRequest,
  InsightApiGetCanAccessActionsRequest,
  InsightApiGetCanAccessRelationSummaryRequest,
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
  MapApiGetAccessGraphRequest,
  ResourceApiGetResourceByUuidRequest,
  RiskContextResponse,
  SearchResponse,
  SearchV2ApiNetworkMapRequest,
  VulnerabilitiesApiGetInstanceSummaryByIdRequest,
  VulnerabilitiesApiGetVulnerabilityByIdRequest,
  InsightApiGetPrivilegeSummaryRequest,
  PrivilegeSummary,
  AppSchemasIacIacDeploymentSummary,
  AudittrailApiGetResourceActionsRequest,
  ResourceAction,
  ResourceApiGetResourceMetadataRequest,
  ResourceMetadata,
} from '@ariksa/inventory-core';
import {
  ConnectionResponse,
  EbpfApiGetUniqueProcessesRequest,
  InsightV2ApiGetVmDeployedByAmiRequest,
  PaginatedInventoryResources,
  PortOpenSummary,
  ProcessResponse,
  ResourceApiGetResourcesByResourceGroupRequest,
  ResourceApiGetResourcesByStorageAccountRequest,
  ResourceSummaryApiGetOpenPortsRequest,
} from '@ariksa/inventory-core/api';
import {
  AlertLogsApiGetLogsForIdsRequest,
  AlertLogsResponse,
  AlertPaginatedResponse,
  AlertsApiGetAlertsRequest,
  AlertsApiGetEntitySeverityListRequest,
  AlertsApiGetEntityTimelineRequest,
  EntitySeverityListResponse,
  EntityTimelineResponse,
  SnoozedAlertsApiAddSnoozedAlertRequest,
  SnoozeDetailsRead,
} from '@ariksa/notification';
import {
  AlertsApiGetAlertBlueprintsRequest,
  BlueprintDetails,
} from '@ariksa/notification/api';
import flagsmith from '@flagsmith/flagsmith';
import { call, select } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';
import {
  DefaultApiGetCsvReportRequest,
  DefaultApiGetCvesByIdRequest,
  DefaultApiGetEcrImageListRequest,
  ItemApiGetContainerImageMetadataRequest,
  ItemApiGetContainersRequest,
  ItemApiGetInstanceSummaryByIdRequest,
  ItemApiGetVulnerabilityByIdRequest,
} from 'services/Vulnerability/legacyRequestTypes';
import {
  ContainerImageMetadataRead,
  ContainerInfo,
  InstanceSummary,
  VulnerabilityCvePage,
  VulnerabilityImageDashboard,
  VulnerabilityPage,
} from 'services/Vulnerability/types';

import {
  AuditTrailApiService,
  authTokenHeader,
  ComplianceService,
  DataScanningService,
  EbpfCollector,
  InventoryService,
  NotificationService,
} from 'api/index';
import {
  getScanVaultResourceFindings,
  getScanVaultResourceSummary,
  getScanVaultCVEDetails,
  getScanVaultContainerImageMetadata,
  getScanVaultContainers,
  getScanVaultContainersForImage,
  getScanVaultVulnerableImages,
  mapScanVaultFindingsToLegacy,
  mapScanVaultResourceSummaryToLegacy,
} from 'api/scan-vault/client';
import { LegacyScanAnalysis } from 'api/services/legacyScanAnalysis';
import { actions } from 'containers/ActiveCloudResource/slice';
import { doGetAlertCount } from 'containers/Dashboard/saga';
import { doGetRiskContext } from 'containers/SharedState/saga';
import { doGetSensitiveDataLabelCount } from 'containers/Visibility/Data/saga';
import { doGetCveHosts } from 'containers/Visibility/Vulnerabilities/saga';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';

const scanVaultFindingCursors = new Map<string, Map<number, string>>();

function scanVaultFindingCursorKey(resourceUUID: string, params: any) {
  return JSON.stringify([
    resourceUUID,
    params.resolved ? 'resolved' : 'open',
    params.resolved_date ?? params.effective_start,
    params.isPatchable,
    params.severity,
    params.vulnerabilityType,
    params.searchTerm,
    params.sortBy,
    params.sortOrder,
  ]);
}

export function* activeResourceSaga() {
  yield takeLatestAction(actions.getResourceDetails, doGetResourceDetails);
  yield takeLatestAction(actions.getResourceInsight, doGetResourceInsight);
  yield takeLatestAction(actions.getRiskContext, doGetRiskContext);
  yield takeLatestAction(actions.getContainerRiskContext, doGetRiskContext);
  yield takeLatestAction(
    actions.getLinkedResourceDetails,
    doGetLinkedResourceDetails,
  );
  yield takeLatestAction(actions.getAlerts, doGetAlerts);
  yield takeLatestAction(actions.getNetworkMap, doGetNetworkMap);
  yield takeLatestAction(actions.getEntityTimeline, doGetEntityTimeline);
  yield takeLatestAction(
    actions.getVulnerabilitySummary,
    doGetVulnerabilitySummary,
  );
  yield takeLatestAction(
    actions.getAMIVulnerabilitySummary,
    doGetAMIVulnerabilitySummary,
  );
  yield takeLatestAction(
    actions.getVulnerabilityDetails,
    doGetVulnerabilityDetails,
  );
  yield takeLatestAction(
    actions.getAMIVulnerabilityDetails,
    doGetAMIVulnerabilityDetails,
  );
  yield takeLatestAction(actions.getEntitySeverity, doGetEntitySeverity);
  yield takeLatestAction(actions.getPiiByBucketName, doGetPiiByBucketName);
  yield takeLatestAction(actions.getCanAccessSummary, doGetCanAccessSummary);
  yield takeLatestAction(
    actions.getAccessByIdentityPage,
    doGetAccessByIdentityPage,
  );
  yield takeLatestAction(actions.getAccessMapData, doGetAccessMapData);
  yield takeLatestAction(actions.getPermissions, doGetInsecureAclUsage);
  yield takeLatestAction(actions.loadRules, doGeRules);
  yield takeLatestAction(
    actions.getS3InternetActivity,
    doGetS3InternetActivity,
  );
  yield takeLatestAction(actions.getS3CrossAccount, doGetS3CrossAccount);
  yield takeLatestAction(actions.getInstanceSummary, doGetInstanceSummaryById);
  yield takeLatestAction(actions.getCrudPermissions, doGetCrudPermissions);
  yield takeLatestAction(
    actions.getECRContainerImages,
    doGetECRContainerImages,
  );
  yield takeLatestAction(
    actions.getContainerVulnerability,
    doGetContainerVulnerability,
  );
  yield takeLatestAction(
    actions.getECRContainerImageVulnerability,
    doGetVulnerabilityDetails,
  );
  yield takeLatestAction(actions.getPiiRiskContext, doGetPiiRiskContext);
  yield takeLatestAction(actions.getAlertStatus, doGetAlertStatus);
  yield takeLatestAction(actions.addException, doAddException);
  yield takeLatestAction(actions.snoozeAlert, doSnoozeAlert);
  yield takeLatestAction(actions.getDeploymentSummary, doGetDeploymentSummary);
  yield takeLatestAction(
    actions.getAccountDeployedResources,
    doGetAccountDeployedResources,
  );
  yield takeLatestAction(
    actions.getRepositoryBranches,
    doGetRepositoryBranches,
  );
  yield takeLatestAction(actions.getAccessed, doGetAccessed);
  yield takeLatestAction(
    actions.getActionsPerformedOnDay,
    doGetActionsPerformedOnDay,
  );
  yield takeLatestAction(
    actions.getResourceActionsMonthly,
    doGetResourceActionsMonthly,
  );
  yield takeLatestAction(
    actions.getResourceActionsDaily,
    doGetResourceActionsDaily,
  );
  yield takeLatestAction(
    actions.getDynamicResourceDetails,
    doGetGcpResourceDetails,
  );
  yield takeLatestAction(actions.getAMIHosts, doGetVmsDeployedByAMI);
  yield takeLatestAction(actions.getAMIHostsRiskContext, doGetRiskContext);
  yield takeLatestAction(
    actions.getRiskContextForResourcesByResourceGroup,
    doGetRiskContext,
  );

  //data tab
  yield takeLatestAction(actions.getDataTypesBySource, doGetDataTypesBySource);
  yield takeLatestAction(actions.getResourcesByGroup, doGetResourcesByGroup);
  yield takeLatestAction(
    actions.getDocumentTypesBySource,
    doGetDocumentTypesBySource,
  );
  yield takeLatestAction(actions.getDataSummary, doGetDataSummary);
  yield takeLatestAction(actions.getDataLabels, doGetSensitiveDataLabelCount);
  yield takeLatestAction(
    actions.getDistributionByTypeOfFiles,
    doGetDistributionByTypeOfFiles,
  );
  yield takeLatestAction(actions.getRunningProcesses, doGetRunningProcesses);
  yield takeLatestAction(
    actions.getECRImageContainers,
    doGetECRImageContainers,
  );
  yield takeLatestAction(
    actions.getProcessConnections,
    doGetProcessConnections,
  );
  yield takeLatestAction(
    actions.getDistributionByDocumentType,
    doGetDistributionByDocumentType,
  );
  yield takeLatestAction(actions.getAlertBlueprints, doGeAlertBlueprints);
  yield takeLatestAction(
    actions.getDataSourcesByStorageAccount,
    doGetDataSourcesByStorageAccount,
  );
  yield takeLatestAction(
    actions.getResourcesByResourceGroup,
    doGetResourcesByResourceGroup,
  );
  yield takeLatestAction(actions.getAlertCount, doGetAlertCount);
  yield takeLatestAction(actions.getOpenPorts, doGetOpenPorts);
  yield takeLatestAction(actions.downloadReport, doDownloadVulnerabilityReport);
  yield takeLatestAction(actions.getTickets, doGetTickets);
  yield takeLatestAction(actions.getCVEHosts, doGetCveHosts);
  yield takeLatestAction(
    actions.getCVEAdditionalDetails,
    doGetCVEAdditionalDetails,
  );
  yield takeLatestAction(
    actions.getContainerImageMetadata,
    doGetContainerImageMetadata,
  );
}

function* doGetContainerImageMetadata(
  ctx: QueryContext<
    ContainerImageMetadataRead,
    ItemApiGetContainerImageMetadataRequest
  >,
) {
  const useScanVault = yield select(
    state => selectVulnerabilities(state).useVulnerabilityV3,
  );
  if (useScanVault && (ctx.params as any)?.resourceUuid) {
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultContainerImageMetadata(
          (ctx.params as any).resourceUuid,
          // The ECR image drawer carries its digest in the legacy containerId
          // slot; ScanVault facts expose it as image_digest.
          (ctx.params as any).containerId,
        ),
      {
        map: value => value,
        errorMsg: 'Failed to get CVE additional details!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getContainerImageMetadata(ctx.params),
    {
      errorMsg: 'Failed to get CVE additional details!',
    },
  );
}

function* doGetCVEAdditionalDetails(
  ctx: QueryContext<VulnerabilityCvePage, DefaultApiGetCvesByIdRequest>,
) {
  const requestFlag = (ctx.params as any)?.useScanVaultVulnerabilityFindings;
  const useScanVault =
    typeof requestFlag === 'boolean'
      ? requestFlag
      : yield select(state => selectVulnerabilities(state).useVulnerabilityV3);
  if (useScanVault) {
    const request = (ctx.params as any)?.cVEByIDRequest ?? {};
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultCVEDetails({
          cveIds: request.vulnerability_ids,
          packageName: request.package_name,
          packageVersion: request.package_version,
          status: 'open',
        }),
      {
        errorMsg: 'Failed to get CVE additional details!',
        map: value => value,
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getCvesById(ctx.params),
    {
      errorMsg: 'Failed to get CVE additional details!',
    },
  );
}

function* doGetProcessConnections(
  ctx: QueryContext<ConnectionResponse, ConnectionByProcessPayload>,
) {
  yield call(
    ctx.fetch,
    () => EbpfCollector.Default.getConnections(ctx.params),
    { errorMsg: 'Failed to get unique processes!' },
  );
}

function* doGetRunningProcesses(
  ctx: QueryContext<ProcessResponse, EbpfApiGetUniqueProcessesRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Ebpf.getUniqueProcesses(ctx.params),
    { errorMsg: 'Failed to get unique processes!' },
  );
}

function* doGetS3InternetActivity(
  ctx: QueryContext<
    S3InternetActivity[],
    AudittrailApiGetS3InternetActivityRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => ComplianceService.AuditTrail.getS3InternetActivity(ctx.params),
    { errorMsg: 'Failed to get internet activity!' },
  );
}

function* doGetS3CrossAccount(
  ctx: QueryContext<
    S3CrossAccountAccess[],
    AudittrailApiGetS3CrossAccountRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => ComplianceService.AuditTrail.getS3CrossAccount(ctx.params),
    { errorMsg: 'Failed to get cross account information!' },
  );
}

function* doGetPiiByBucketName(
  ctx: QueryContext<
    PageFileMetaDataInfo,
    VisibilityDataApiGetFileMetadataInfoRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Visibility.getFileMetadataInfo(ctx.params),
    { errorMsg: 'Failed to get sensitive data information!' },
  );
}

export function* doGetPiiRiskContext(
  ctx: QueryContext<
    Record<string, RiskContextResponse[]>,
    ContextApiGetRiskContextDataScanRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Context.getRiskContextDataScan(ctx.params),
    {
      errorMsg: 'Failed to get risk context!',
    },
  );
}

function* doGetAlerts(
  ctx: QueryContext<AlertPaginatedResponse, AlertsApiGetAlertsRequest>,
) {
  yield call(
    ctx.fetch,
    () => NotificationService.Alerts.getAlerts(ctx.params),
    { errorMsg: 'Failed to get alerts!' },
  );
}

export function* doGetResourceDetails(
  ctx: QueryContext<any, ResourceApiGetResourceByUuidRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Resource.getResourceByUuid(ctx.params),
    {
      map: r => r.data.results,
      errorMsg: 'Failed to get resource details!',
    },
  );
}

export function* doGetResourceInsight(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2Request>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.InsightV2.insightV2(ctx.params),
    { errorMsg: 'Failed to get resource insights!' },
  );
}

function* doGetLinkedResourceDetails(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiInsightV2Request>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.InsightV2.insightV2(ctx.params),
    { errorMsg: 'Failed to get resource details!', map: r => r },
  );
}

function* doGetEntityTimeline(
  ctx: QueryContext<
    EntityTimelineResponse[],
    AlertsApiGetEntityTimelineRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => NotificationService.Alerts.getEntityTimeline(ctx.params),
    { errorMsg: 'Failed to get entity timeline!' },
  );
}

export function* doGetVulnerabilitySummary(
  ctx: QueryContext<InstanceSummary, ItemApiGetInstanceSummaryByIdRequest>,
) {
  const requestFlag = (ctx.params as any)?.useScanVaultVulnerabilityFindings;
  const useScanVault =
    typeof requestFlag === 'boolean'
      ? requestFlag
      : yield select(state => selectVulnerabilities(state).useVulnerabilityV3);
  const resourceUUID = (ctx.params as any)?.resourceUuid;
  if (useScanVault && resourceUUID) {
    yield call(
      ctx.fetch as any,
      () => getScanVaultResourceSummary(resourceUUID),
      {
        map: mapScanVaultResourceSummaryToLegacy,
        errorMsg: 'Failed to get vulnerability summary!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getInstanceSummaryById(ctx.params),
    { errorMsg: 'Failed to get vulnerability summary!' },
  );
}

function* doGetAMIVulnerabilitySummary(
  ctx: QueryContext<
    InstanceSummary,
    VulnerabilitiesApiGetInstanceSummaryByIdRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Vulnerability.getInstanceSummaryById(ctx.params),
    { errorMsg: 'Failed to get vulnerability summary!' },
  );
}

export function* doGetVulnerabilityDetails(
  ctx: QueryContext<VulnerabilityPage, ItemApiGetVulnerabilityByIdRequest>,
) {
  const useScanVault =
    (ctx.params as any)?.useScanVaultVulnerabilityFindings === true;
  const resourceUUID = (ctx.params as any)?.resourceUuid;
  if (useScanVault && resourceUUID && !paramsHasContainer(ctx.params)) {
    const params: any = ctx.params;
    let pageNumber = params.page ?? 1;
    const pageSize = params.pageSize ?? params.size ?? 20;
    const cursorKey = scanVaultFindingCursorKey(resourceUUID, params);
    let pageCursors = scanVaultFindingCursors.get(cursorKey);
    if (!pageCursors) {
      if (scanVaultFindingCursors.size >= 100) {
        const oldestKey = scanVaultFindingCursors.keys().next().value;
        if (oldestKey) scanVaultFindingCursors.delete(oldestKey);
      }
      pageCursors = new Map<number, string>();
      scanVaultFindingCursors.set(cursorKey, pageCursors);
    }
    let cursor = pageNumber > 1 ? pageCursors.get(pageNumber) : undefined;
    if (pageNumber > 1 && !cursor) {
      // A changed filter/sort starts a new cursor chain. Reset the legacy
      // numbered pager rather than accidentally fetching page one as page N.
      pageNumber = 1;
      cursor = undefined;
      ctx.updateState({
        page: { page_number: 1, page_size: pageSize },
      } as any);
    }
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultResourceFindings(resourceUUID, {
          cursor,
          status: params.resolved ? 'resolved' : 'open',
          from: params.resolved_date ?? params.effective_start,
          limit: pageSize,
          severity: params.severity,
          patchable: params.isPatchable,
          packageCategory: ['os', 'application', 'library'].includes(
            params.vulnerabilityType,
          )
            ? params.vulnerabilityType
            : undefined,
        }),
      {
        map: value => {
          if (value.next_cursor) {
            pageCursors!.set(pageNumber + 1, value.next_cursor);
          } else {
            for (const cachedPage of pageCursors!.keys()) {
              if (cachedPage > pageNumber) pageCursors!.delete(cachedPage);
            }
          }
          return mapScanVaultFindingsToLegacy(
            value,
            pageNumber,
            pageSize,
            params.sortBy,
            params.sortOrder,
          );
        },
        errorMsg: 'Failed to get vulnerability details!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getVulnerabilityById(ctx.params),
    { errorMsg: 'Failed to get vulnerability details!' },
  );
}

function paramsHasContainer(params: unknown): boolean {
  return Boolean((params as any)?.containerId);
}

export function* doGetAMIVulnerabilityDetails(
  ctx: QueryContext<
    VulnerabilityPage,
    VulnerabilitiesApiGetVulnerabilityByIdRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Vulnerability.getVulnerabilityById(ctx.params),
    { errorMsg: 'Failed to get vulnerability details!' },
  );
}

export function* doGetEntitySeverity(
  ctx: QueryContext<
    EntitySeverityListResponse,
    AlertsApiGetEntitySeverityListRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => NotificationService.Alerts.getEntitySeverityList(ctx.params),
    { errorMsg: 'Failed to get alert severity count!' },
  );
}

function* doGetCanAccessSummary(
  ctx: QueryContext<any, InsightApiGetCanAccessRelationSummaryRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Insight.getCanAccessRelationSummary(ctx.params),
    { errorMsg: 'Failed to get can access summary!' },
  );
}

// One page of the Principals tab, from the paginated access-by-identity API.
function* doGetAccessByIdentityPage(
  ctx: QueryContext<AccessByIdentityPage, InsightApiGetAccessByIdentityRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Insight.getAccessByIdentity(ctx.params),
    { errorMsg: 'Failed to get principals!' },
  );
}

function* doGetAccessMapData(
  ctx: QueryContext<SearchResponse, MapApiGetAccessGraphRequest>,
) {
  yield call(ctx.fetch, () => InventoryService.Map.getAccessGraph(ctx.params), {
    errorMsg: 'Failed to get access map!',
  });
}

function* doGetInsecureAclUsage(
  ctx: QueryContext<
    S3InsecureACL[],
    AudittrailApiGetInsecureAclResponseRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => ComplianceService.AuditTrail.getInsecureAclResponse(ctx.params),
    { errorMsg: 'Failed to get insecure ACL usage for this bucket!' },
  );
}

function* doGetInstanceSummaryById(
  ctx: QueryContext<InstanceSummary, ItemApiGetInstanceSummaryByIdRequest>,
) {
  const requestFlag = (ctx.params as any)?.useScanVaultVulnerabilityFindings;
  const useScanVault =
    typeof requestFlag === 'boolean'
      ? requestFlag
      : yield select(state => selectVulnerabilities(state).useVulnerabilityV3);
  if (useScanVault && (ctx.params as any)?.resourceUuid) {
    yield call(
      ctx.fetch as any,
      () => getScanVaultResourceSummary((ctx.params as any).resourceUuid),
      {
        map: mapScanVaultResourceSummaryToLegacy,
        errorMsg: 'Failed to get instance summary!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getInstanceSummaryById(ctx.params),
    { errorMsg: 'Failed to get instance summary!' },
  );
}

function* doGetContainerVulnerability(
  ctx: QueryContext<ContainerInfo[], ItemApiGetContainersRequest>,
) {
  const useScanVault = yield select(
    state => selectVulnerabilities(state).useVulnerabilityV3,
  );
  if (useScanVault && (ctx.params as any)?.resourceUuid) {
    const request = (ctx.params as any)?.getContainersRequest ?? {};
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultContainers((ctx.params as any).resourceUuid, {
          imageNames: request.image_names,
          repositoryUri: request.repository_uri,
          imageDigest: request.image_digest,
          vulnerable: request.vulnerable,
        }),
      { map: value => value, errorMsg: 'Failed to get vulnerabilities!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getContainers(ctx.params),
    {
      errorMsg: 'Failed to get vulnerabilities!',
    },
  );
}

export function* doGetECRContainerImages(
  ctx: QueryContext<
    VulnerabilityImageDashboard,
    DefaultApiGetEcrImageListRequest
  >,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(
      ctx.fetch as any,
      () => getScanVaultVulnerableImages(ctx.params as any),
      { map: value => value, errorMsg: 'Failed to get ECR images!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getEcrImageList(ctx.params),
    { errorMsg: 'Failed to get ECR images!' },
  );
}

/*function* doGetECRContainerImageVulnerability(
  ctx: QueryContext<
    Dict<any>[],
    VulnerabilitiesApiGetImageVulnerabilitiesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getVulnerabilityById(ctx.params),
    { errorMsg: 'Failed to get vulnerabilities!' },
  );
}*/

function* doGetNetworkMap(
  ctx: QueryContext<SearchResponse, SearchV2ApiNetworkMapRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.SearchServiceV2.networkMap(ctx.params),
    {
      errorMsg: 'Failed to get network map!',
    },
  );
}

function* doGetAlertStatus(
  ctx: QueryContext<AlertLogsResponse[], AlertLogsApiGetLogsForIdsRequest>,
) {
  yield call(
    ctx.fetch,
    () => NotificationService.AlertsLogs.getLogsForIds(ctx.params),
    { errorMsg: 'Failed to alert status!' },
  );
}

export function* doAddException(
  ctx: QueryContext<
    BlueprintExceptionRead,
    PolicyExceptionApiAddExceptionRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => ComplianceService.PolicyException.addException(ctx.params),
    { errorMsg: 'Failed to add exception!' },
  );
}

export function* doSnoozeAlert(
  ctx: QueryContext<SnoozeDetailsRead, SnoozedAlertsApiAddSnoozedAlertRequest>,
) {
  yield call(
    ctx.fetch,
    () => NotificationService.SnoozedAlerts.addSnoozedAlert(ctx.params),
    { errorMsg: 'Failed to snooze alert!' },
  );
}

export function* doGetRepositoryBranches(
  ctx: QueryContext<
    RepositoryBranchesInfo[],
    VisibilityDataApiGetRepositoryBranchesInfoRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Visibility.getRepositoryBranchesInfo(ctx.params),
    { errorMsg: 'Failed to get repository branches!' },
  );
}

export function* doGetPrivilegeSummary(
  ctx: QueryContext<PrivilegeSummary[], InsightApiGetPrivilegeSummaryRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Insight.getPrivilegeSummary(ctx.params),
    { errorMsg: 'Failed to get privilege summary!' },
  );
}

export function* doGetDeploymentSummary(
  ctx: QueryContext<
    AppSchemasIacIacDeploymentSummary[],
    IACApiDeploymetSummaryRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Iac.deploymetSummary(ctx.params),
    { errorMsg: 'Failed to get privilege summary!' },
  );
}

export function* doGetAccountDeployedResources(
  ctx: QueryContext<DeployedResourcePagination, IACApiDeployedResourcesRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      InventoryService.Iac.deployedResources({
        ...ctx.params,
        page: ctx.page.page_number,
        pageSize: ctx.page.page_size,
      }),
    { errorMsg: 'Failed to get deployment resources!' },
  );
}

function* doGetCrudPermissions(
  ctx: QueryContext<ActionsResponse, InsightApiGetCanAccessActionsRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Insight.getCanAccessActions(ctx.params),
    {
      cacheKey: 'doGetCrudPermissions',
    },
  );
}

export function* doGetResourceActionsMonthly(
  ctx: QueryContext<
    V1ResourceActionsMonthlyResponse,
    AuditTrailServiceApiAuditTrailServiceResourceActionsMonthlyRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceResourceActionsMonthly(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get monthly actions on resource!',
      // cacheKey: 'doGetAccessed',
    },
  );
}

export function* doGetResourceActionsDaily(
  ctx: QueryContext<
    V1ResourceActionsDailyResponse,
    AuditTrailServiceApiAuditTrailServiceResourceActionsDailyRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceResourceActionsDaily(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get daily actions on resource!',
      // cacheKey: 'doGetAccessed',
    },
  );
}

export function* doGetAccessed(
  ctx: QueryContext<
    V1ActionsPerformedMonthlyResponse,
    AuditTrailServiceApiAuditTrailServiceActionsPerformedMonthlyRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceActionsPerformedMonthly(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get monthly actions performed!',
      // cacheKey: 'doGetAccessed',
    },
  );
}

export function* doGetActionsPerformedOnDay(
  ctx: QueryContext<
    V1ActionsPerformedOnDayResponse,
    AuditTrailServiceApiAuditTrailServiceActionsPerformedOnDayRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      AuditTrailApiService.AuditTrail.auditTrailServiceActionsPerformedOnDay(
        ctx.params,
        {
          ...authTokenHeader(),
        },
      ),
    {
      errorMsg: 'Failed to get daily actions performed!',
      // cacheKey: 'doGetAccessed',
    },
  );
}

export function* doGetResourceActions(
  ctx: QueryContext<ResourceAction[], AudittrailApiGetResourceActionsRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.AudittrailApi.getResourceActions(ctx.params),
    {
      errorMsg: 'Failed to get actions performed on the resource!',
      cacheKey: 'doGetResourceActions',
    },
  );
}

export function* doGetDataTypesBySource(
  ctx: QueryContext<DataTypeInfo[], DataTypesApiGetDataTypesBySourceRequest>,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.DataTypes.getDataTypesBySource(ctx.params),
    {
      errorMsg: 'Failed to get data types by source!',
    },
  );
}

export function* doGetResourcesByGroup(
  ctx: QueryContext<PageResourcesRead, ResourcesApiGetResourcesByGroupRequest>,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getResourcesByGroup(ctx.params),
    {
      errorMsg: 'Failed to get resources by group!',
    },
  );
}

export function* doGetDocumentTypesBySource(
  ctx: QueryContext<
    PageDocumentClassificationForSourceInfo,
    VisibilityDataApiGetDocumentClassificationByTypeForSourceRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      DataScanningService.Visibility.getDocumentClassificationByTypeForSource(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get document types by source!',
    },
  );
}

export function* doGetDataSummary(
  ctx: QueryContext<
    SourceCountSummary,
    ResourcesApiGetSummarySourcesCountRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getSummarySourcesCount(ctx.params),
    {
      errorMsg: 'Failed to get data summary!',
    },
  );
}

export function* doGetDistributionByTypeOfFiles(
  ctx: QueryContext<
    ObjectsDistributionCount,
    ResourcesApiGetObjectsDistributionCountRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => DataScanningService.Resources.getObjectsDistributionCount(ctx.params),
    {
      errorMsg: 'Failed to get distribution by type of files!',
    },
  );
}

export function* doGetDistributionByDocumentType(
  ctx: QueryContext<
    DistributionByDocumentType,
    VisibilityDataApiGetDocumentClassificationTypeDistributionForSourceRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      DataScanningService.Visibility.getDocumentClassificationTypeDistributionForSource(
        ctx.params,
      ),
    {
      errorMsg: 'Failed to get distribution by document type!',
    },
  );
}

export function* doGetGcpResourceDetails(
  ctx: QueryContext<ResourceMetadata, ResourceApiGetResourceMetadataRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Resource.getResourceMetadata(ctx.params),
    {
      errorMsg: 'Failed to get resource details!',
    },
  );
}

export function* doGetECRImageContainers(
  ctx: QueryContext<ContainerInfo[], ItemApiGetContainersRequest>,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    const request = (ctx.params as any)?.getContainersRequest ?? {};
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultContainersForImage({
          imageNames: request.image_names,
          repositoryUri: request.repository_uri,
          imageDigest: request.image_digest,
        }),
      { map: value => value, errorMsg: 'Failed to get containers!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Item.getContainers(ctx.params),
    {
      errorMsg: 'Failed to get containers!',
    },
  );
}

export function* doGetVmsDeployedByAMI(
  ctx: QueryContext<InsightV2Pagination, InsightV2ApiGetVmDeployedByAmiRequest>,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.InsightV2.getVmDeployedByAmi(ctx.params),
    {
      errorMsg: 'Failed to get VMs deployed by AMI!',
    },
  );
}

export function* doGeRules(
  ctx: QueryContext<
    PageComplianceReport,
    ComplianceApiGetComplianceReportForResourceRequest
  >,
) {
  yield call(ctx.fetch, () =>
    ComplianceService.Compliance.getComplianceReportForResource(ctx.params),
  );
}

export function* doGeAlertBlueprints(
  ctx: QueryContext<BlueprintDetails[], AlertsApiGetAlertBlueprintsRequest>,
) {
  yield call(ctx.fetch, () =>
    NotificationService.Alerts.getAlertBlueprints(ctx.params),
  );
}

export function* doGetResourcesByResourceGroup(
  ctx: QueryContext<
    PaginatedInventoryResources,
    ResourceApiGetResourcesByResourceGroupRequest
  >,
) {
  yield call(ctx.fetch, () =>
    InventoryService.Resource.getResourcesByResourceGroup(ctx.params),
  );
}

export function* doGetDataSourcesByStorageAccount(
  ctx: QueryContext<
    PaginatedInventoryResources,
    ResourceApiGetResourcesByStorageAccountRequest
  >,
) {
  yield call(ctx.fetch, () =>
    InventoryService.Resource.getResourcesByStorageAccount(ctx.params),
  );
}

export function* doGetOpenPorts(
  ctx: QueryContext<PortOpenSummary[], ResourceSummaryApiGetOpenPortsRequest>,
) {
  yield call(ctx.fetch, () =>
    InventoryService.ResourceSummary.getOpenPorts(ctx.params),
  );
}

export function* doDownloadVulnerabilityReport(
  ctx: QueryContext<any, DefaultApiGetCsvReportRequest>,
) {
  if (flagsmith.hasFeature('new_vuln_dashboard')) {
    yield call(
      ctx.fetch as any,
      () =>
        Promise.reject(
          new Error('Vulnerability report export is temporarily unavailable.'),
        ),
      { errorMsg: 'Vulnerability report export is temporarily unavailable.' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getCsvReport(ctx.params),
    {
      errorMsg: 'Failed to download report!',
    },
  );
}

export function* doGetTickets(ctx: QueryContext<any, any>) {
  /*yield call(ctx.fetch, () => NotificationService.AlertsLogs.getTicketSummary(ctx.params), {
    errorMsg: 'Failed to get tickets for a resource!',
  });*/
}
